import { useEffect, useRef, useState } from 'react';

import type { DecodePuzzleDefinition, QualityTier } from '@aztec-realms/shared-types';
import { useSceneStartup } from '../SceneStartup';

import {
  mountDecodeRenderer,
  type DecodeRendererController,
  type DecodeRendererState,
  type DecodeVisualEvent,
} from './decode-renderer';

interface DecodeCanvasProps {
  puzzle: DecodePuzzleDefinition;
  qualityTier: QualityTier;
  rotations: Readonly<Record<string, number>>;
  reducedMotion: boolean;
  selectedSegmentId?: string;
  solved: boolean;
  suggestedSegmentId?: string;
  onSegmentSelected: (segmentId: string) => void;
  onVisualEvent?: (event: DecodeVisualEvent) => void;
  onReady?: () => void;
}

export function DecodeCanvas({
  puzzle,
  qualityTier,
  rotations,
  reducedMotion,
  selectedSegmentId,
  solved,
  suggestedSegmentId,
  onSegmentSelected,
  onVisualEvent,
  onReady,
}: DecodeCanvasProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<DecodeRendererController | null>(null);
  const initialStateRef = useRef<DecodeRendererState>({
    rotations,
    selectedSegmentId,
    solved,
    suggestedSegmentId,
  });
  const currentStateRef = useRef<DecodeRendererState>({
    rotations,
    selectedSegmentId,
    solved,
    suggestedSegmentId,
  });
  const [status, setStatus] = useState<'loading' | 'ready' | 'fallback'>('loading');
  useSceneStartup(
    'decode',
    status === 'ready',
    status === 'fallback'
      ? 'Your glyph could not load. Try again; your puzzle progress is kept.'
      : undefined,
  );
  const [visualAnnouncement, setVisualAnnouncement] = useState('');
  const [zoom, setZoom] = useState(1);
  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || navigator.userAgent.includes('jsdom')) {
      setStatus('fallback');
      onReady?.();
      return;
    }
    let cancelled = false;
    void mountDecodeRenderer(host, puzzle, initialStateRef.current, {
      qualityTier,
      reducedMotion,
      onSegmentSelected,
      onVisualEvent: (event) => {
        const message =
          event.name === 'segment_rotate'
            ? 'Stone segment rotated and snapped into place.'
            : event.name === 'connection_made'
              ? 'A new energy connection formed.'
              : event.name === 'dead_end'
                ? 'That channel reaches a dead end.'
                : event.name === 'glyph_resonance'
                  ? 'The central glyph is resonating.'
                  : event.name === 'puzzle_solved'
                    ? 'The glyph restoration is visually complete.'
                    : 'A rare reward is ready to reveal.';
        setVisualAnnouncement(message);
        onVisualEvent?.(event);
      },
      onReady: () => {
        if (cancelled) return;
        setStatus('ready');
        onReady?.();
      },
      onUnavailable: () => {
        if (!cancelled) setStatus('fallback');
      },
    })
      .then((controller) => {
        if (cancelled) controller.destroy();
        else {
          controllerRef.current = controller;
          controller.update(currentStateRef.current);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('fallback');
        onReady?.();
      });
    return () => {
      cancelled = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
      host.replaceChildren();
    };
  }, [onReady, onSegmentSelected, onVisualEvent, puzzle, qualityTier, reducedMotion]);

  useEffect(() => {
    const nextState: DecodeRendererState = {
      rotations,
      selectedSegmentId,
      solved,
      suggestedSegmentId,
    };
    currentStateRef.current = nextState;
    controllerRef.current?.update(nextState);
  }, [rotations, selectedSegmentId, solved, suggestedSegmentId]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const frame = window.requestAnimationFrame(() => {
      controllerRef.current?.resize();
      viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2);
      viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [zoom]);

  const zoomPercent = Math.round(zoom * 100);
  const updateZoom = (next: number) => setZoom(Math.max(1, Math.min(1.7, next)));

  return (
    <section
      aria-label={`${puzzle.familyLabel} glyph mechanism`}
      className="decode-canvas"
      data-solved={solved || undefined}
      data-zoomed={zoom > 1 || undefined}
    >
      <div className="decode-canvas__viewport" ref={viewportRef}>
        <div
          aria-hidden="true"
          className="decode-canvas__host"
          ref={hostRef}
          style={{ height: `${zoomPercent}%`, width: `${zoomPercent}%` }}
        />
        {status === 'loading' && (
          <span className="canvas-status">Activating the glyph tablet…</span>
        )}
        {status === 'fallback' && (
          <div
            className="decode-fallback"
            role="img"
            aria-label="A concentric carved stone glyph mechanism with jade energy channels"
          >
            <span />
            <span />
            <span />
          </div>
        )}
      </div>
      <div aria-label="Puzzle zoom" className="decode-zoom-controls" role="group">
        <button
          aria-label="Zoom puzzle out"
          disabled={zoom <= 1}
          onClick={() => updateZoom(zoom - 0.35)}
          type="button"
        >
          −
        </button>
        <output aria-live="polite" aria-label={`Puzzle zoom ${zoomPercent}%`}>
          {zoomPercent}%
        </output>
        <button
          aria-label="Zoom puzzle in"
          disabled={zoom >= 1.7}
          onClick={() => updateZoom(zoom + 0.35)}
          type="button"
        >
          +
        </button>
      </div>
      <span aria-live="polite" className="ar-visually-hidden" role="status">
        {visualAnnouncement ||
          (status === 'ready'
            ? 'Glyph mechanism ready.'
            : 'Accessible segment controls are available beside the board.')}
      </span>
    </section>
  );
}
